import { Children, FC, memo, useCallback } from 'react';
import { Box, Button, Skeleton, Stack } from '@chakra-ui/react';
import { useSplunkActivityLogger } from '@resolutionlife/ui';
import { isEmpty, repeat, takeLast } from 'ramda';
import { useNavigate } from 'react-router-dom';

import { EventFragment, EventType } from '../../../graphql/generated';
import { navigateToDirectory } from '../../../pages/file-browser-page/file-browser-page';
import { navigateToFileDetailsRoute } from '../../../pages/file-details-page/file-details-page';
import { DebounceLoadingState } from '../../../ui/debounce-loading/debounce-loading';
import { EmptyState } from '../../../ui/errors/empty-state';
import { ErrorState } from '../../../ui/errors/error-state';

import { ActivityFeedProps } from '../../file-activity-panel/file-activity-panel';
import {
  getDateLabel,
  groupByDate,
} from '../../file-activity-panel/format-events';
import { DateHeader } from '../date-header';
import { Event } from '../event';
import { useFileActivityEvents } from '../use-file-activity-events/use-file-activity-events';

type Props = Pick<ActivityFeedProps, 'cedant' | 'onClose'> & {
  eventType?: EventType;
};

export const FileActivityList: FC<Props> = ({ cedant, eventType, onClose }) => {
  const navigate = useNavigate();
  const activityLogger = useSplunkActivityLogger();
  const {
    loading,
    error,
    cedantName,
    events,
    isFetchingMore,
    hasMore,
    fetchMore,
  } = useFileActivityEvents(cedant, eventType);

  const handleNavigateToParentFolder = useCallback(
    (event: EventFragment) => () => {
      onClose();

      const parentDirPath = navigateToDirectory({
        cedantId: cedant,
        directoryId: event.storageObject.parentDirectoryId,
      });

      navigate(parentDirPath);
    },
    [cedant, navigate, onClose]
  );

  const handleNavigateToFolder = useCallback(
    (event: EventFragment) => () => {
      onClose();

      const dirPath = navigateToDirectory({
        cedantId: cedant,
        directoryId: event.storageObject.id,
      });

      navigate(dirPath);
    },
    [cedant, navigate, onClose]
  );

  const handleNavigateToFile = useCallback(
    (event: EventFragment) => () => {
      onClose();

      const fileDetailsPath = navigateToFileDetailsRoute({
        cedantId: cedant,
        fileId: event.storageObject.id,
      });

      navigate(fileDetailsPath);
    },
    [cedant, navigate, onClose]
  );

  if (loading && !isFetchingMore) {
    return (
      <DebounceLoadingState>
        <Stack flex={1} spacing="6">
          {Children.toArray(repeat(<Skeleton height="20" />, 8))}
        </Stack>
      </DebounceLoadingState>
    );
  }

  if (error || !events || !cedantName) {
    return (
      <ErrorState
        title="Unable to load file activity"
        subtitle="Please reach out to the technical support team for further assistance."
      />
    );
  }

  if (isEmpty(events)) {
    return (
      <EmptyState
        title="No file activity to display"
        subtitle={
          eventType
            ? undefined
            : 'Events will appear once files have been uploaded.'
        }
      />
    );
  }

  const groupedEvents = groupByDate(events);
  const eventsTuple = Object.entries(groupedEvents);
  const nextPageCursor = takeLast(1, events)[0].id;
  return (
    <Stack spacing="8" data-testid="activity-feed-list">
      {eventsTuple.map(([date, eventsGroup]) => (
        <Box key={date}>
          <DateHeader title={getDateLabel(date)} />
          <Stack spacing="4" pt="4">
            {eventsGroup.map((event) => (
              <Event
                key={event.id}
                event={event}
                cedantName={cedantName}
                onClickFile={handleNavigateToFile}
                onClickFolder={handleNavigateToFolder}
                onClickParentFolder={handleNavigateToParentFolder}
              />
            ))}
          </Stack>
        </Box>
      ))}
      {hasMore && (
        <Button
          isLoading={isFetchingMore}
          data-testid="fetch-more-btn"
          onClick={() => {
            activityLogger.logActivityFeedPaginate({ cedantId: cedant, cursor: nextPageCursor });
            fetchMore(nextPageCursor)();
          }}
        >
          Show more
        </Button>
      )}
    </Stack>
  );
};

export const MemoizedFileActivityList = memo(FileActivityList);
